/**
 * 표준사전 클라이언트 — QBase 에 이미 등록된 조회 API 를 그대로 쓴다 (QBase 코드 변경 없음).
 *
 *   GET /qcore/home/mdlrmdl/std/termLst   → 표준용어  (고객번호 ↔ CUST_NO ↔ 도메인 ↔ 정보유형)
 *   GET /qcore/home/mdlrmdl/std/wdLst     → 표준단어  (고객 ↔ CUST)  — 물리명 분해용
 *   GET /qcore/home/mdlrmdl/std/inftpLst  → 정보유형  (명V100 → VARCHAR(100)) — 타입·길이
 *
 * 로컬(스탠드얼론) 모드에는 QBase 가 없으므로 **빈 사전**을 돌려주고, 매핑 엔진은 이름·샘플만으로
 * 동작한다. 사전은 세션 동안 한 번만 받아 캐시한다.
 */
import { IS_LOCAL } from '../../../config/mode';
import { qbaseGet } from '../../../services/qbaseClient';

export interface StdTerm {
  /** 용어명(논리) — 예 '고객번호' */ termNm: string;
  /** 용어 약어(물리) — 예 'CUST_NO' */ termAbbrNm: string;
  dmnNm?: string;
  /** 정보유형 — 예 'IDV30' */ inftpNm?: string;
  meanCn?: string;
}
export interface StdWord { wdNm: string; wdAbbrNm: string; wdEngCn?: string }
export interface StdInftp { inftpNm: string; datpNm?: string; datpLen?: string; dmnGrpNm?: string }

export interface StdDictionary {
  loaded: boolean;
  stdNm: string;
  /** 물리명(정규화) → 용어 */
  byAbbr: Map<string, StdTerm>;
  /** 논리명(정규화) → 용어 */
  byName: Map<string, StdTerm>;
  /** 정보유형명 → 타입·길이 */
  inftp: Map<string, { base: string; length?: number }>;
  /** 단어 약어(정규화) → 한글 단어 */
  wordByAbbr: Map<string, string>;
  /** 한글 단어 → 약어 */
  wordByName: Map<string, string>;
}

export const EMPTY_DICTIONARY: StdDictionary = {
  loaded: false, stdNm: '',
  byAbbr: new Map(), byName: new Map(), inftp: new Map(), wordByAbbr: new Map(), wordByName: new Map(),
};

/** 이름 비교용 정규화 — 대소문자·밑줄·공백·하이픈을 지운다 */
export function normalizeName(s?: string): string {
  return String(s || '').toLowerCase().replace(/[\s_\-.]/g, '');
}

let cache: { key: string; value: Promise<StdDictionary> } | null = null;

/** 표준사전을 읽어 색인한다. QBase 모드가 아니면 빈 사전. */
export function loadStdDictionary(stdNm = '시스템'): Promise<StdDictionary> {
  if (IS_LOCAL) return Promise.resolve(EMPTY_DICTIONARY);
  if (cache && cache.key === stdNm) return cache.value;
  const value = fetchDictionary(stdNm).catch(() => EMPTY_DICTIONARY);   // 권한·네트워크 실패는 폴백
  cache = { key: stdNm, value };
  return value;
}

export function clearStdDictionaryCache(): void { cache = null; }

async function fetchDictionary(stdNm: string): Promise<StdDictionary> {
  const params = { 표준명: stdNm, 사용자ID: '' } as Record<string, string>;
  const [termRes, wdRes, inftpRes] = await Promise.all([
    qbaseGet('/mdlrmdl/std/termLst', params).catch(() => ({})),
    qbaseGet('/mdlrmdl/std/wdLst', params).catch(() => ({})),
    qbaseGet('/mdlrmdl/std/inftpLst', params).catch(() => ({})),
  ]);
  const terms: StdTerm[] = (termRes as any)?.termLst || [];
  const words: StdWord[] = (wdRes as any)?.wdLst || [];
  const inftps: StdInftp[] = (inftpRes as any)?.inftpLst || [];

  const dict: StdDictionary = {
    loaded: terms.length > 0 || words.length > 0,
    stdNm,
    byAbbr: new Map(), byName: new Map(), inftp: new Map(), wordByAbbr: new Map(), wordByName: new Map(),
  };
  terms.forEach((t) => {
    if (t.termAbbrNm) dict.byAbbr.set(normalizeName(t.termAbbrNm), t);
    if (t.termNm) dict.byName.set(normalizeName(t.termNm), t);
  });
  words.forEach((w) => {
    if (w.wdAbbrNm) dict.wordByAbbr.set(normalizeName(w.wdAbbrNm), w.wdNm);
    if (w.wdNm) dict.wordByName.set(w.wdNm, w.wdAbbrNm);
  });
  inftps.forEach((i) => {
    if (!i.inftpNm) return;
    const len = i.datpLen ? Number(i.datpLen) : undefined;
    dict.inftp.set(i.inftpNm, { base: (i.datpNm || '').toUpperCase(), length: Number.isFinite(len) ? len : undefined });
  });
  return dict;
}

/** 등록된 표준 목록 — 표준이 여럿인 사이트에서 고르게 한다 (실패하면 빈 배열) */
export async function listStandards(): Promise<string[]> {
  if (IS_LOCAL) return [];
  try {
    const r: any = await qbaseGet('/mdlrmdl/std/termLst', { 표준명: '', 사용자ID: '' });
    const list: StdTerm[] = r?.termLst || [];
    return Array.from(new Set(list.map((t: any) => t.stdNm).filter(Boolean)));
  } catch { return []; }
}

/**
 * 범용 약어표 — 표준사전이 없거나(스탠드얼론) 사전에 빠진 조각을 위한 폴백.
 * 국내 시스템에서 관례적으로 쓰는 약어만 담는다. 사전이 있으면 사전이 우선한다.
 */
const COMMON_ABBR: Record<string, string> = {
  no: '번호', num: '번호', nm: '명', name: '명', id: 'ID',
  dt: '일자', date: '일자', ymd: '일자', tm: '시각', time: '시각', dttm: '일시',
  cd: '코드', code: '코드', amt: '금액', amount: '금액', qty: '수량', quantity: '수량',
  cnt: '건수', count: '건수', yn: '여부', flag: '여부', desc: '설명', dc: '설명',
  cust: '고객', customer: '고객', ord: '주문', order: '주문', prod: '상품', product: '상품',
  addr: '주소', address: '주소', tel: '전화', email: '이메일', stat: '상태', status: '상태',
  price: '금액', sum: '합계', total: '합계', seq: '순번', reg: '등록', upd: '수정',
};

/**
 * 물리명을 표준단어로 분해한다 — `CUST_CNTRT_NO` → ['고객','계약','번호'].
 * 표준사전 → 범용 약어표 순으로 찾고, 둘 다 없으면 원문을 남긴다(약한 신호로만 쓰인다).
 * 구분자가 없는 낙타표기(CustomerName)도 잘라 낸다.
 */
function splitParts(physical: string): string[] {
  const raw = String(physical || '');
  let parts = raw.split(/[_\-\s.]+/).filter(Boolean);
  if (parts.length === 1) {
    // camelCase / PascalCase 분리 — CustomerName → [Customer, Name]
    const camel = raw.split(/(?<=[a-z0-9])(?=[A-Z])/).filter(Boolean);
    if (camel.length > 1) parts = camel;
  }
  return parts;
}

export function splitByWords(physical: string, dict: StdDictionary): string[] {
  return splitParts(physical).map((p) => {
    const key = normalizeName(p);
    return dict.wordByAbbr.get(key) || COMMON_ABBR[key] || p;
  });
}

/**
 * 조각마다 **동의어 집합**을 만든다 — [원문, 표준사전 단어, 범용 약어표 단어].
 *
 * 조각을 하나의 값으로 확정하면, 한쪽만 표준사전에 있고 다른 쪽은 약어표로 바뀌는 순간
 * (예: 사전에 'ORD' 만 있고 'ORDER' 는 없는 경우) 같은 뜻인데도 서로 달라져 매칭이 끊긴다.
 * 집합으로 두고 교집합으로 비교하면 어느 경로로 바뀌든 만난다.
 */
export function splitByWordSets(physical: string, dict: StdDictionary): string[][] {
  return splitParts(physical).map((p) => {
    const key = normalizeName(p);
    const set = new Set<string>([key]);
    const fromDict = dict.wordByAbbr.get(key);
    if (fromDict) { set.add(normalizeName(fromDict)); const abbr = dict.wordByName.get(fromDict); if (abbr) set.add(normalizeName(abbr)); }
    const common = COMMON_ABBR[key];
    if (common) set.add(normalizeName(common));
    // 한글 단어로 들어온 경우 그 약어도 (고객 → CUST)
    const abbrOfName = dict.wordByName.get(p);
    if (abbrOfName) set.add(normalizeName(abbrOfName));
    return Array.from(set);
  });
}
